import { ErrorModal, useUserContext } from "@dexteel/mesf-core";
import { Grid2 } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { AgGridReact } from "ag-grid-react";
import { useState } from "react";
import { Section, SectionGap } from "../../../models/Section";
import { getSectionGaps } from "../../../repositories/SectionRepository";
import { SectionGapModal } from "./SectionGapModal";
import { useSectionsGapsGridData } from "./useSectionGapsGridData";

type useSectionGapsProps = {
	sectionId?: number | null;
	onError: (error: Error) => void;
	onSuccess?: (data: any[]) => void;
};

export const useSectionGaps = ({
	sectionId,
	onError,
	onSuccess,
}: useSectionGapsProps) => {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	return useQuery<SectionGap[], Error>({
		queryKey: ["sectionGaps", sectionId],
		queryFn: ({ signal }) =>
			getSectionGaps({ sectionId, defaultAreaId }, signal),
		onError: onError,
		onSuccess: onSuccess,
	});
};

type Props = {
	section: Section;
	disabled?: boolean;
};

export const SectionGapsGrid = ({ section, disabled = false }: Props) => {
	const [error, setError] = useState("");
	const [openEdit, setOpenEdit] = useState<boolean>(false);
	const [selectedData, setSelectedData] = useState<SectionGap | null>();

	const { data: sectionGaps, isLoading } = useSectionGaps({
		sectionId: section?.SectionId,
		onError: (error) => setError(error.message),
	});

	const { columnDefs } = useSectionsGapsGridData({
		setOpenEdit,
		setSelectedData,
	});
	const { defaultColDef } = useGridDefinitions({});

	return (
		<>
			<Grid2
				size={{ xs: 12, md: 12 }}
				sx={{
					height: "40.6vh",
				}}
			>
				<div style={{ height: "100%", width: "100%" }}>
					<AgGridReact<SectionGap>
						loading={isLoading}
						rowData={sectionGaps}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						rowHeight={28}
						headerHeight={30}
						animateRows
						suppressContextMenu
						loadingOverlayComponent={CenteredLazyLoading}
						getRowId={(params) =>
							`${params.data.SectionId} ${params.data.StandId}`
						}
						gridOptions={{
							rowSelection: {
								mode: "singleRow",
								enableClickSelection: true,
								checkboxes: false,
							},
						}}
					/>
				</div>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<SectionGapModal
				show={openEdit}
				onHide={() => {
					setOpenEdit(false);
					setSelectedData(null);
				}}
				sectionGap={selectedData!}
			/>
		</>
	);
};
